import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Divider,
  Typography,
  Grid,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ButtonBase } from "@mui/material";
import { IconEdit, IconTrash, IconPlus} from "@tabler/icons-react";
import MainCard from "ui-component/cards/MainCard";
import CurrencyTextField from "components/text-fields/currency-text-field";

const EditButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  width: 40,
  height: 40,
  borderRadius: 10,
  cursor: "pointer",
}));

const DeleteButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.error.dark,
  "&:hover": {
    background: theme.palette.error.dark,
    color: theme.palette.error.light,
  },
  width: 40,
  height: 40,
  borderRadius: 12,
  cursor: "pointer",
}));

const AddButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.info.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  align: "center",
  gap: 8,
  width: 200,
  height: 40,
  borderRadius: 10,
  display: "flex",
  cursor: "pointer",
}));

const formatCurrency = (value) => {
  if (!value) return "";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

const formatPercentage = (value) => {
  if (!value) return "";
  return new Intl.NumberFormat("pt-BR", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
};

const BankAnnualDataList = () => {
  const location = useLocation();
  const [annualData, setAnnualData] = useState([]);
  const [banks, setBanks] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentData, setCurrentData] = useState({
    bank: null,
    year: new Date().getFullYear(),
    profit_loss_value: "",
    customer_account_value: "",
    pdd_provision_refund_value: "",
    pdd_provision_value: "",
    pdd_reversal_value: "",
    default_percentage: "",
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [selectedBankName, setSelectedBankName] = useState("");

  // Extrair o parâmetro bank da URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const bankId = urlParams.get('bank');
    if (bankId) {
      setSelectedBankId(bankId);
    }
  }, [location.search]);

  // Atualizar o nome do banco selecionado quando os bancos forem carregados
  useEffect(() => {
    if (selectedBankId && banks.length > 0) {
      const selectedBank = banks.find(bank => bank.id === parseInt(selectedBankId));
      if (selectedBank) {
        setSelectedBankName(`${selectedBank.code} - ${selectedBank.name}`);
      }
    }
  }, [selectedBankId, banks]);

  // Buscar bancos para o autocomplete
  const fetchBanks = useCallback(() => {
    fetch("/backend/banks/?page_size=1000")
      .then((response) => response.json())
      .then((data) => {
        setBanks(data.results || []);
      })
      .catch((error) => console.error("Erro ao carregar bancos:", error));
  }, []);

  const fetchAnnualData = useCallback(() => {
    // Só busca dados se há um banco selecionado
    if (!selectedBankId) {
      setAnnualData([]);
      setTotalRecords(0);
      return;
    }

    let url = `/backend/dados-anuais/?page=${page + 1}&page_size=${rowsPerPage}`;
    url += `&bank=${selectedBankId}`;
    
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Falha ao carregar os dados anuais");
        }
        return response.json();
      })
      .then((data) => {
        setAnnualData(data.results);
        setTotalRecords(data.count);
      })
      .catch((error) => {
        console.error("Erro ao carregar dados anuais:", error);
        alert("Erro ao carregar dados anuais: " + error.message);
      });
  }, [page, rowsPerPage, selectedBankId]);

  useEffect(() => {
    fetchBanks();
  }, [fetchBanks]);

  useEffect(() => {
    fetchAnnualData();
  }, [fetchAnnualData, page, rowsPerPage, selectedBankId]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleEdit = (data) => {
    setCurrentData({
      ...data,
      bank: banks.find(bank => bank.id === data.bank) || null,
    });
    setIsEditing(true);
    setOpenModal(true);
  };

  const handleAdd = () => {
    const selectedBank = selectedBankId ? banks.find(bank => bank.id === parseInt(selectedBankId)) : null;
    setCurrentData({
      bank: selectedBank,
      year: new Date().getFullYear(),
      profit_loss_value: "",
      customer_account_value: "",
      pdd_provision_refund_value: "",
      pdd_provision_value: "",
      pdd_reversal_value: "",
      default_percentage: "",
    });
    setIsEditing(false);
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleSave = () => {
    if (!currentData.bank) {
      alert("Erro: banco não identificado.");
      return;
    }

    const dataToSave = {
      bank: currentData.bank.id,
      year: currentData.year,
      profit_loss_value: currentData.profit_loss_value || null,
      customer_account_value: currentData.customer_account_value || null,
      pdd_provision_refund_value: currentData.pdd_provision_refund_value || null,
      pdd_provision_value: currentData.pdd_provision_value || null,
      pdd_reversal_value: currentData.pdd_reversal_value || null,
      default_percentage: currentData.default_percentage || null,
    };

    const method = isEditing ? "PUT" : "POST";
    const url = isEditing
      ? `/backend/dados-anuais/${currentData.id}/`
      : "/backend/dados-anuais/";

    fetch(url, {
      method: method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dataToSave),
    })
      .then((response) => {
        if (response.ok) return response.json();
        throw new Error("Erro ao salvar dados anuais");
      })
      .then((data) => {
        alert("Dados anuais salvos com sucesso!");
        setOpenModal(false);
        fetchAnnualData();
      })
      .catch((error) => {
        console.error("Erro ao salvar:", error);
        alert("Erro: " + error.message);
      });
  };

  const handleDelete = (dataId) => {
    if (window.confirm("Você tem certeza que deseja excluir estes dados anuais?")) {
      fetch(`/backend/dados-anuais/${dataId}/`, {
        method: "DELETE",
      })
        .then((response) => {
          if (response.ok) {
            alert("Dados anuais excluídos com sucesso!");
            fetchAnnualData();
          } else {
            throw new Error("Erro ao excluir dados anuais");
          }
        })
        .catch((error) => {
          console.error("Erro ao excluir:", error);
          alert("Erro: " + error.message);
        });
    }
  };

  return (
    <div className="w-full overflow-auto">
      <MainCard
        title={selectedBankName ? `Dados anuais do banco ${selectedBankName}` : "Dados Anuais dos Bancos"}
        actions={[
          <AddButton onClick={handleAdd}>
            <IconPlus stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
            Adicionar dados anuais
          </AddButton>,
        ]}
      >
        {selectedBankId ? (
          <Divider sx={{ my: 2 }} />
        ) : (
          <Typography variant="body1" sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
            Selecione um banco para visualizar os dados anuais.
          </Typography>
        )}
        {selectedBankId && (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ano</TableCell>
                <TableCell align="right">Lucro/Prejuízo</TableCell>
                <TableCell align="right">Conta Clientes</TableCell>
                <TableCell align="right">Estorno Provisões</TableCell>
                <TableCell align="right">Provisão</TableCell>
                <TableCell align="right">Reversão</TableCell>
                <TableCell align="right">% Inadimplência</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {annualData.map((data) => (
                <TableRow key={data.id}>
                  <TableCell>{data.year}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(data.profit_loss_value)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(data.customer_account_value)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(data.pdd_provision_refund_value)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(data.pdd_provision_value)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(data.pdd_reversal_value)}
                  </TableCell>
                  <TableCell align="right">
                    {formatPercentage(data.default_percentage)}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <EditButton onClick={() => handleEdit(data)}>
                        <IconEdit stroke={1.5} size="1.3rem" />
                      </EditButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <DeleteButton onClick={() => handleDelete(data.id)}>
                        <IconTrash stroke={1.5} size="1.3rem" />
                      </DeleteButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </MainCard>

      {selectedBankId && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Registros por página"
        />
      )}

      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditing ? "Editar Dados Anuais" : "Adicionar Dados Anuais"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {/* Ano */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Ano"
                type="number"
                value={currentData.year}
                onChange={(e) =>
                  setCurrentData({ ...currentData, year: parseInt(e.target.value) })
                }
                fullWidth
                sx={{ mt: 2 }}
                required
                inputProps={{ min: 1900, max: new Date().getFullYear() + 10 }}
              />
            </Grid>

            {/* Lucro/Prejuízo */}
            <Grid item xs={12}>
              <CurrencyTextField
                label="Valor Lucro/Prejuízo"
                value={currentData.profit_loss_value}
                onChange={(value) =>
                  setCurrentData({ ...currentData, profit_loss_value: value })
                }
                fullWidth
                placeholder="R$ 0,00"
              />
            </Grid>

            {/* Conta Clientes */}
            <Grid item xs={12}>
              <CurrencyTextField
                label="Valor Conta Clientes"
                value={currentData.customer_account_value}
                onChange={(value) =>
                  setCurrentData({ ...currentData, customer_account_value: value })
                }
                fullWidth
                placeholder="R$ 0,00"
              />
            </Grid>

            {/* Estorno de Provisões */}
            <Grid item xs={12}>
              <CurrencyTextField
                label="Valor Estorno de Provisões"
                value={currentData.pdd_provision_refund_value}
                onChange={(value) =>
                  setCurrentData({ ...currentData, pdd_provision_refund_value: value })
                }
                fullWidth
                placeholder="R$ 0,00"
              />
            </Grid>

            {/* Provisão */}
            <Grid item xs={12}>
              <CurrencyTextField
                label="Valor Provisão"
                value={currentData.pdd_provision_value}
                onChange={(value) =>
                  setCurrentData({ ...currentData, pdd_provision_value: value })
                }
                fullWidth
                placeholder="R$ 0,00"
              />
            </Grid>

            {/* Reversão */}
            <Grid item xs={12}>
              <CurrencyTextField
                label="Valor Reversão"
                value={currentData.pdd_reversal_value}
                onChange={(value) =>
                  setCurrentData({ ...currentData, pdd_reversal_value: value })
                }
                fullWidth
                placeholder="R$ 0,00"
              />
            </Grid>

            {/* Percentual de Inadimplência */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Percentual de Inadimplência"
                type="number"
                value={currentData.default_percentage}
                onChange={(e) =>
                  setCurrentData({ ...currentData, default_percentage: e.target.value })
                }
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                placeholder="0,00"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleSave} color="primary">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BankAnnualDataList;